// Requêtes du plan précis des marchés à ciel ouvert (D-300 à D-308) : îlots, couche du plan par
// emprise, ajustements des places, recensement, commerçants et imports.
import { useQuery } from '@tanstack/react-query';
import type { Coord, Numerotation, RapportImport } from '@cc/contrats';
import type { FeatureCollection, Polygon } from 'geojson';
import { requete } from '../../api/client';

export interface Ilot {
  id: string;
  marche_id: string;
  code: string;
  nom: string;
  rangees: number;
  places_par_rangee: number;
  largeur_type_m: number;
  profondeur_type_m: number;
  numerotation: Numerotation;
  precision: 'schematique' | 'terrain';
  version: number;
  coins: Coord[];
  releve_le: string | null;
  avancement?: {
    places: number;
    recensees: number;
    occupees: number;
    attribuees: number;
    doublons: number;
  };
}

export interface AvancementRecensement {
  ilots: {
    id: string | null;
    code: string;
    nom: string;
    places: number;
    recensees: number;
    occupees: number;
    libres: number;
  }[];
  agents: {
    id: string | null;
    nom: string;
    passages: number;
    places: number;
    doublons: number;
    dernier: string;
  }[];
  doublons: {
    id: string;
    place: { id: string; reference: string };
    statut: string;
    occupant: string | null;
    telephone: string | null;
    motif: 'occupant_different' | 'commercant_deja_recense';
    concurrent: { statut: string; occupant: string | null } | null;
    homonyme: { nom: string; place: string | null } | null;
    recu_le: string;
  }[];
}

export interface Commercant {
  id: string;
  nom: string;
  telephone: string | null;
  activite: string | null;
  source: 'recensement' | 'import';
  place: { id: string; reference: string } | null;
  doublon_signale: boolean;
  doublon_decision: string | null;
  recensement_id: string | null;
  attribution: { id: string; numero: string } | null;
}

export interface LigneHistorique {
  id: string;
  action: string;
  reference_avant: string | null;
  reference_apres: string | null;
  motif: string | null;
  cree_le: string;
  auteur: string | null;
}

type Bbox = [number, number, number, number];

export function useIlots(marcheId: string) {
  return useQuery({
    queryKey: ['marches', marcheId, 'ilots'],
    queryFn: ({ signal }) =>
      requete<{ elements: Ilot[] }>(`/marches/${marcheId}/ilots`, { signal }).then(
        (r) => r.elements,
      ),
  });
}

export function usePlanPrecis(marcheId: string, bbox: Bbox | null) {
  return useQuery({
    queryKey: ['marches', marcheId, 'plan', bbox],
    enabled: Boolean(bbox),
    retry: false,
    queryFn: ({ signal }) =>
      requete<{ ilots: FeatureCollection<Polygon>; formes: FeatureCollection<Polygon> }>(
        `/carte/marches/${marcheId}/plan`,
        {
          signal,
          parametres: { bbox: bbox!.join(',') },
        },
      ),
  });
}

export function useRecensement(marcheId: string) {
  return useQuery({
    queryKey: ['marches', marcheId, 'recensement'],
    queryFn: ({ signal }) =>
      requete<AvancementRecensement>(`/marches/${marcheId}/recensement`, { signal }),
  });
}

export function useCommercants(marcheId: string, q: string) {
  return useQuery({
    queryKey: ['marches', marcheId, 'commercants', q],
    queryFn: ({ signal }) =>
      requete<{ elements: Commercant[] }>(`/marches/${marcheId}/commercants`, {
        signal,
        parametres: q ? { q } : {},
      }).then((r) => r.elements),
  });
}

export function useHistoriquePlace(id: string | null) {
  return useQuery({
    queryKey: ['marches', 'historique', id],
    enabled: Boolean(id),
    queryFn: ({ signal }) =>
      requete<{ elements: LigneHistorique[] }>(`/marches/emplacements/${id}/historique`, {
        signal,
      }).then((r) => r.elements),
  });
}

const poster = <R>(chemin: string, corps: unknown, methode: 'POST' | 'PATCH' = 'POST') =>
  requete<R>(chemin, { methode, corps });

export const creerIlot = (marcheId: string, corps: Record<string, unknown>) =>
  poster<{ id: string; code: string; places: number }>(`/marches/${marcheId}/ilots`, corps);
export const renumeroterIlot = (
  id: string,
  corps: { numerotation: Numerotation; motif: string; version_attendue: number },
) => poster<{ renommees: number }>(`/marches/ilots/${id}/renumerotation`, corps);
export const ajusterPlace = (id: string, corps: Record<string, unknown>) =>
  poster(`/marches/emplacements/${id}/ajustement`, corps);
export const scinderPlace = (id: string, corps: { parts: number; version_attendue: number }) =>
  poster<{ creees: { reference: string }[] }>(`/marches/emplacements/${id}/scission`, corps);
export const fusionnerPlaces = (places: { id: string; version_attendue: number }[]) =>
  poster('/marches/emplacements/fusion', { places });
export const retirerPlace = (id: string, corps: { motif: string; version_attendue: number }) =>
  poster(`/marches/emplacements/${id}/retrait`, corps);
export const renumeroterPlace = (
  id: string,
  corps: { reference: string; motif: string; version_attendue: number },
) => poster(`/marches/emplacements/${id}/reference`, corps, 'PATCH');
export const importerPlan = (
  marcheId: string,
  corps: { format: 'geojson' | 'kml'; contenu: string; appliquer: boolean; rubrique_code: string },
) => poster<RapportImport>(`/marches/${marcheId}/import-plan`, corps);
export const importerCommercants = (
  marcheId: string,
  corps: { contenu: string; appliquer: boolean },
) => poster<RapportImport>(`/marches/${marcheId}/import-commercants`, corps);
export const deciderRecensement = (
  id: string,
  corps: { decision: 'retenir' | 'ecarter' | 'distinct' | 'meme_personne'; motif: string },
) => poster(`/marches/recensements/${id}/decision`, corps);
export const attribuerRecensement = (
  id: string,
  corps: { periode: { debut: string; fin: string } },
) => poster<{ id: string; numero: string }>(`/marches/recensements/${id}/attribution`, corps);
